import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MwButton, MwSpinnerButton, TruncatedText } from '../components/ui'
import { useCurrent, useFetch } from '../hooks'
import { getCatalogLabel, getPriorityLabel, getStatusAppearance, getStatusLabel, normalizeSupportSettings } from '../constants/ticket'
import { MdPrint, MdFileDownload, MdUploadFile, MdClose } from 'react-icons/md'
import { formatAssigneeNames, getTicketAssignees } from '../utils/ticketAssignees';
import { apiClient, buildAuthRequestConfig } from '../utils/apiClient';
import { useSearchParams } from 'react-router-dom';
import { applyTicketDateParams, formatTicketPeriodRange, normalizeTicketPeriod, TICKET_PERIODS } from '../utils/ticketDateRange';
import { buildTicketFilterOptions, TICKET_FILTER_KEYS } from '../utils/ticketFilterOptions';
import TicketFiltersBar from '../components/tickets/TicketFiltersBar';
import ReportsPager from '../components/reports/ReportsPager';
import TicketImportModal from '../components/reports/TicketImportModal';

const TABLE_PAGE_SIZE = 80;
const EXPORT_PAGE_SIZE = 100; // أقصى limit بيسمح بيه /admin

const Reports = () => {
  const { user, currentLangId } = useCurrent()
  const [t] = useTranslation('global')
  const isAr = currentLangId === 'ar';
  const isTruthyFlag = (value) => value === true || value === 'true' || value === 1 || value === '1';

  // --- الفلاتر محفوظة في رابط الصفحة زي /tickets (?period=month&product=...) — الافتراضي هذا الشهر ---
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(
    () => TICKET_FILTER_KEYS.reduce((acc, key) => ({ ...acc, [key]: searchParams.get(key) || 'all' }), {}),
    [searchParams]
  );
  const period = useMemo(() => ({
    period: normalizeTicketPeriod(searchParams.get('period')),
    from: searchParams.get('from') || '',
    to: searchParams.get('to') || ''
  }), [searchParams]);
  const reportStatus = searchParams.get('status') || 'all';
  const debouncedSearch = String(searchParams.get('q') || '').trim();
  const [reportSearch, setReportSearch] = useState(() => searchParams.get('q') || '');

  const updateParams = (mutate) => {
    const next = new URLSearchParams(searchParams.toString());
    mutate(next);
    setSearchParams(next, { replace: true });
  };

  // البحث بيتكتب في الرابط بعد ما المستخدم يوقف كتابة شوية، عشان منعملش طلب مع كل حرف.
  useEffect(() => {
    const trimmed = reportSearch.trim();
    if (trimmed === debouncedSearch) return undefined;
    const timer = window.setTimeout(() => {
      const next = new URLSearchParams(searchParams.toString());
      if (trimmed) next.set('q', trimmed);
      else next.delete('q');
      setSearchParams(next, { replace: true });
    }, 400);
    return () => window.clearTimeout(timer);
  }, [debouncedSearch, reportSearch, searchParams, setSearchParams]);

  // --- بيانات مساعدة ---
  const { data: supportSettingsResult } = useFetch(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/settings`, true);
  const { data: statusesResult } = useFetch(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/statuses/admin`, true);
  const { data: tenantsResult } = useFetch(`${process.env.REACT_APP_Auth_API_BASE_URL}/tenants/getAll`, true);

  const supportSettings = useMemo(() => ({
    ...normalizeSupportSettings(supportSettingsResult),
    statuses: normalizeSupportSettings({ data: { statuses: statusesResult?.data || [] } }).statuses
  }), [statusesResult?.data, supportSettingsResult]);

  const tenants = useMemo(() => Array.isArray(tenantsResult?.data) ? tenantsResult.data : [], [tenantsResult?.data]);
  const scopedTenants = useMemo(() => {
    if (user?.role === 'admin') return tenants;
    if (isTruthyFlag(user?.supportAdminEnabled) && isTruthyFlag(user?.supportSuperAdminEnabled)) return tenants;
    if (!isTruthyFlag(user?.supportAdminEnabled) || !isTruthyFlag(user?.supportTenantAdminEnabled)) return tenants;
    const scope = String(user?.supportTenantScope || 'own');
    if (scope === 'all') return tenants;
    if (scope === 'selected') {
      const allowedIds = Array.isArray(user?.supportTenantIds) ? user.supportTenantIds.map(String) : [];
      return tenants.filter((tenant) => allowedIds.includes(String(tenant?._id)));
    }
    const ownTenantId = user?.tenant_id?._id || user?.tenant_id || null;
    if (!ownTenantId) return [];
    return tenants.filter((tenant) => String(tenant?._id) === String(ownTenantId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenants, user]);

  // نفس صلاحية فلتر المعيّن في /tickets
  const canFilterByAssignee =
    user?.role === 'admin' ||
    (isTruthyFlag(user?.supportAdminEnabled) && isTruthyFlag(user?.supportSuperAdminEnabled));

  const { data: adminsResult } = useFetch(
    canFilterByAssignee ? `${process.env.REACT_APP_Auth_API_BASE_URL}/users/support-admins` : null,
    Boolean(canFilterByAssignee)
  );

  const filterOptions = useMemo(() => buildTicketFilterOptions({
    supportSettings,
    tenants: scopedTenants,
    admins: Array.isArray(adminsResult?.data) ? adminsResult.data : [],
    lang: currentLangId,
    t,
    canFilterByAssignee,
  }), [adminsResult, canFilterByAssignee, currentLangId, scopedTenants, supportSettings, t]);

  // --- بارامترات السيرفر (نفس /tickets: الفلاتر + البحث + التاريخ) ---
  const filterQuery = useMemo(() => {
    const apiParams = new URLSearchParams();
    TICKET_FILTER_KEYS.forEach((key) => {
      if (key === 'assignee' && !canFilterByAssignee) return;
      const value = String(filters[key] || '').trim();
      if (value && value !== 'all') apiParams.set(key, value);
    });
    if (debouncedSearch) apiParams.set('q', debouncedSearch);
    applyTicketDateParams(apiParams, new URLSearchParams({ period: period.period, from: period.from || '', to: period.to || '' }));
    return apiParams.toString();
  }, [canFilterByAssignee, debouncedSearch, filters, period]);

  // عدّادات الحالات من السيرفر بنفس الفلاتر — نفس مصدر /tickets
  const { data: statsResult } = useFetch(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/admin/stats?${filterQuery}`);
  const statusStats = statsResult?.data || null;

  const fetchAdminTicketsPage = async (page, limit) => {
    const apiParams = new URLSearchParams(filterQuery);
    apiParams.set('limit', String(limit));
    apiParams.set('page', String(page));
    if (reportStatus && reportStatus !== 'all') apiParams.set('status', reportStatus);
    const response = await apiClient.get(
      `${process.env.REACT_APP_SUPPORT_API_BASE_URL}/admin?${apiParams.toString()}`,
      buildAuthRequestConfig()
    );
    return {
      rows: Array.isArray(response?.data?.data) ? response.data.data : [],
      total: Number(response?.data?.pagination?.total || 0),
      totalPages: Math.max(1, Number(response?.data?.pagination?.totalPages || 1)),
    };
  };

  // --- الجدول: صفحة واحدة بس من السيرفر (pagination) ---
  const [page, setPage] = useState(1);
  const [reportRows, setReportRows] = useState([]);
  const [reportTotal, setReportTotal] = useState(0);
  const [reportLoading, setReportLoading] = useState(true);
  const [reportError, setReportError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const requestIdRef = useRef(0);

  // أي تغيير في الفلاتر يرجّع لأول صفحة
  useEffect(() => { setPage(1); }, [filterQuery, reportStatus]);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    const load = async () => {
      setReportLoading(true);
      setReportError(null);
      try {
        const result = await fetchAdminTicketsPage(page, TABLE_PAGE_SIZE);
        if (requestId !== requestIdRef.current) return;
        setReportRows(result.rows);
        setReportTotal(result.total);
        // لو الصفحة الحالية بقت بعد آخر صفحة (مثلاً بعد استيراد/حذف) نرجع لآخر صفحة
        if (page > result.totalPages) setPage(result.totalPages);
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        setReportRows([]);
        setReportTotal(0);
        setReportError(err);
      } finally {
        if (requestId === requestIdRef.current) setReportLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterQuery, reportStatus, page, reloadKey]);

  // --- تحديد تذاكر معيّنة (بيفضل محفوظ وانت بتتنقل بين الصفحات) ---
  const [selected, setSelected] = useState(() => new Map());
  const pageIds = reportRows.map((ticket) => String(ticket?._id));
  const pageSelectedCount = pageIds.filter((id) => selected.has(id)).length;
  const allPageSelected = pageIds.length > 0 && pageSelectedCount === pageIds.length;
  const toggleTicket = (ticket) => setSelected((prev) => {
    const next = new Map(prev);
    const id = String(ticket?._id);
    if (next.has(id)) next.delete(id);
    else next.set(id, ticket);
    return next;
  });
  const togglePage = () => setSelected((prev) => {
    const next = new Map(prev);
    if (allPageSelected) reportRows.forEach((ticket) => next.delete(String(ticket?._id)));
    else reportRows.forEach((ticket) => next.set(String(ticket?._id), ticket));
    return next;
  });
  const selectAllPageRef = useRef(null);
  useEffect(() => {
    if (selectAllPageRef.current) selectAllPageRef.current.indeterminate = pageSelectedCount > 0 && !allPageSelected;
  }, [pageSelectedCount, allPageSelected]);

  // التصدير/الطباعة: التذاكر المحددة، ولو مفيش تحديد كل النتائج المطابقة (بتتحمّل صفحة ورا صفحة وقت الطلب).
  const [exportProgress, setExportProgress] = useState(null);
  const loadRowsForExport = async () => {
    if (selected.size) return Array.from(selected.values());
    const collected = [];
    let current = 1;
    let totalPages = 1;
    do {
      const result = await fetchAdminTicketsPage(current, EXPORT_PAGE_SIZE);
      collected.push(...result.rows);
      totalPages = result.totalPages;
      setExportProgress({ done: current, total: totalPages });
      current += 1;
    } while (current <= totalPages);
    return collected;
  };

  const getStatusCount = (value) => {
    if (value === 'all') return Number(statusStats?.total || 0);
    return Number(statusStats?.statusCounts?.[value] || 0);
  };

  const formatReportDate = (value) => {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const getReportTicketRow = (ticket) => {
    const section = getCatalogLabel(supportSettings?.sections, ticket?.sectionId, currentLangId);
    const product = getCatalogLabel(supportSettings?.products, ticket?.ProductId, currentLangId);
    const statusValue = ticket?.statusId || ticket?.status;
    const status = getStatusLabel(supportSettings?.statuses, statusValue, currentLangId);
    const tenantName = ticket?.tenantName || scopedTenants.find((tenant) => String(tenant?._id) === String(ticket?.tenantId))?.tenantName || ticket?.tenantId || '';
    const assignee = formatAssigneeNames(getTicketAssignees(ticket).map((entry) => entry.name || entry.userId), currentLangId);
    const lastModifiedAdmin = ticket?.lastModifiedBy
      ? (filterOptions.assignee || []).find((opt) => String(opt.value) === String(ticket.lastModifiedBy))
      : null;
    return {
      ticketNumber: ticket?.ticketNumber || '',
      subject: ticket?.subject || '',
      tenant: tenantName,
      assignee,
      section,
      product,
      priority: getPriorityLabel(ticket?.priority || 'medium', currentLangId),
      visibility: String(ticket?.visibility || 'external').toLowerCase() === 'internal' ? (isAr ? 'داخلي' : 'Internal') : (isAr ? 'خارجي' : 'External'),
      createdAt: formatReportDate(ticket?.createdAt),
      updatedAt: formatReportDate(ticket?.modifiedAt || ticket?.updatedAt),
      expectedAt: formatReportDate(ticket?.expectedAt),
      lastModifiedBy: ticket?.lastModifiedByName || lastModifiedAdmin?.label || '',
      status,
      // قيم خام لملف Excel (رقم حقيقي وتواريخ حقيقية بدل نصوص)
      ticketNumberRaw: Number.isFinite(Number(ticket?.ticketNumber)) ? Number(ticket.ticketNumber) : (ticket?.ticketNumber || ''),
      createdAtRaw: toExcelDate(ticket?.createdAt),
      expectedAtRaw: toExcelDate(ticket?.expectedAt),
      updatedAtRaw: toExcelDate(ticket?.modifiedAt || ticket?.updatedAt),
    };
  };

  // exceljs بيكتب التواريخ على إنها UTC — بنزوّد فرق التوقيت عشان الساعة تطلع زي اللي في الشاشة.
  function toExcelDate(value) {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return '';
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  }

  // أعمدة التقرير — نفس الترتيب في الجدول والطباعة وملف Excel، والعناوين حسب لغة الواجهة.
  const getReportColumns = () => [
    { key: 'ticketNumber', head: isAr ? 'رقم التذكرة' : 'Ticket #', value: (r) => r.ticketNumber, excel: (r) => r.ticketNumberRaw, width: 12, center: true },
    { key: 'subject', head: isAr ? 'عنوان التذكرة' : 'Subject', value: (r) => r.subject, width: 44 },
    { key: 'tenant', head: isAr ? 'العميل' : 'Customer', value: (r) => r.tenant, width: 26 },
    ...(canFilterByAssignee ? [{ key: 'assignee', head: isAr ? 'معينة لـ' : 'Assigned To', value: (r) => r.assignee, width: 26 }] : []),
    { key: 'section', head: isAr ? 'القسم' : 'Section', value: (r) => r.section, width: 20 },
    { key: 'product', head: isAr ? 'المنتج / الخدمة' : 'Product', value: (r) => r.product, width: 22 },
    { key: 'priority', head: isAr ? 'الأولوية' : 'Priority', value: (r) => r.priority, width: 16, center: true },
    { key: 'visibility', head: isAr ? 'نوع التذكرة' : 'Ticket type', value: (r) => r.visibility, width: 13, center: true },
    { key: 'status', head: isAr ? 'الحالة' : 'Status', value: (r) => r.status, width: 16, center: true },
    { key: 'createdAt', head: isAr ? 'تاريخ الإنشاء' : 'Created At', value: (r) => r.createdAt, excel: (r) => r.createdAtRaw, width: 18, center: true },
    { key: 'expectedAt', head: isAr ? 'الوقت المتوقع' : 'Expected time', value: (r) => r.expectedAt, excel: (r) => r.expectedAtRaw, width: 18, center: true },
    { key: 'updatedAt', head: isAr ? 'آخر تحديث' : 'Updated At', value: (r) => r.updatedAt, excel: (r) => r.updatedAtRaw, width: 18, center: true },
    { key: 'lastModifiedBy', head: isAr ? 'آخر تعديل بواسطة' : 'Last modified by', value: (r) => r.lastModifiedBy, width: 22 },
  ];

  // ملخص الفلاتر المطبّقة — بيتكتب أعلى ملف الـ Excel والطباعة عشان يبان التقرير متعمل على إيه.
  const getFilterSummary = (count) => {
    const labelOf = (key) => {
      const list = filterOptions[key] || [];
      return (list.find((opt) => String(opt.value) === String(filters[key] || 'all')) || list[0])?.label || '';
    };
    const periodMeta = TICKET_PERIODS.find((item) => item.value === period.period);
    const periodRange = formatTicketPeriodRange(period.period, period.from, period.to, currentLangId);
    return [
      [isAr ? 'الفترة الزمنية' : 'Date range', `${periodMeta ? (isAr ? periodMeta.ar : periodMeta.en) : ''}${periodRange ? ` (${periodRange})` : ''}`],
      [isAr ? 'العميل' : 'Customer', labelOf('tenantId')],
      [isAr ? 'المنتج / الخدمة' : 'Product', labelOf('product')],
      [isAr ? 'القسم' : 'Section', labelOf('section')],
      [isAr ? 'الأولوية' : 'Priority', labelOf('priority')],
      [isAr ? 'نوع التذكرة' : 'Ticket type', labelOf('visibility')],
      ...(canFilterByAssignee ? [[isAr ? 'المعيّن' : 'Assignee', labelOf('assignee')]] : []),
      [isAr ? 'الحالة' : 'Status', reportStatus === 'all' ? (isAr ? 'كل الحالات' : 'All statuses') : getStatusLabel(supportSettings?.statuses, reportStatus, currentLangId)],
      ...(debouncedSearch ? [[isAr ? 'البحث' : 'Search', debouncedSearch]] : []),
      ...(selected.size ? [[isAr ? 'التذاكر' : 'Tickets', isAr ? 'تذاكر محددة يدوياً' : 'Manually selected tickets']] : []),
      [isAr ? 'عدد التذاكر' : 'Tickets count', String(count)],
      [isAr ? 'تاريخ التصدير' : 'Exported at', formatReportDate(new Date())],
    ];
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const escapeCell = (value) => String(value || '').replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');

  const buildReportTable = (tickets) => {
    const columns = getReportColumns();
    const tableRows = tickets.map(getReportTicketRow)
      .map((r) => `<tr>${columns.map((col) => `<td>${escapeCell(col.value(r))}</td>`).join('')}</tr>`)
      .join('');
    const head = columns.map((col) => `<th>${escapeCell(col.head)}</th>`).join('');
    const summary = getFilterSummary(tickets.length)
      .map(([label, value]) => `<tr><td class="lbl">${escapeCell(label)}</td><td>${escapeCell(value)}</td></tr>`)
      .join('');
    return { head, body: tableRows, summary, colCount: columns.length };
  };

  // ملف xlsx حقيقي (exceljs بيتحمّل بس لما المستخدم يضغط تصدير). اتجاه الشيت واللغة حسب لغة الواجهة.
  const [exporting, setExporting] = useState(false);
  const exportReportExcel = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const { default: ExcelJS } = await import('exceljs');
      const title = isAr ? 'تقرير التذاكر' : 'Tickets Report';
      const columns = getReportColumns();
      const tickets = await loadRowsForExport();
      const rows = tickets.map(getReportTicketRow);
      const summary = getFilterSummary(tickets.length);
      const align = { horizontal: isAr ? 'right' : 'left', vertical: 'middle', readingOrder: isAr ? 'rtl' : 'ltr', wrapText: true };
      const border = { style: 'thin', color: { argb: 'FFCBD5E1' } };
      const cellBorder = { top: border, left: border, bottom: border, right: border };

      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'Absat Support';
      workbook.created = new Date();
      const sheet = workbook.addWorksheet(isAr ? 'التذاكر' : 'Tickets', {
        views: [{ rightToLeft: isAr }],
        properties: { defaultRowHeight: 20 },
      });
      sheet.columns = columns.map((col) => ({ width: col.width || 18 }));
      const lastCol = columns.length;

      // العنوان
      sheet.mergeCells(1, 1, 1, lastCol);
      const titleCell = sheet.getCell(1, 1);
      titleCell.value = title;
      titleCell.font = { bold: true, size: 16, color: { argb: 'FF08203F' } };
      titleCell.alignment = { ...align, wrapText: false };
      sheet.getRow(1).height = 28;

      // ملخص الفلاتر
      let rowIndex = 3;
      summary.forEach(([label, value]) => {
        const labelCell = sheet.getCell(rowIndex, 1);
        labelCell.value = label;
        labelCell.font = { bold: true };
        labelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
        labelCell.alignment = align;
        labelCell.border = cellBorder;
        sheet.mergeCells(rowIndex, 2, rowIndex, Math.min(lastCol, 5));
        const valueCell = sheet.getCell(rowIndex, 2);
        valueCell.value = value;
        valueCell.alignment = align;
        valueCell.border = cellBorder;
        rowIndex += 1;
      });

      // جدول التذاكر
      rowIndex += 1;
      const headerRowIndex = rowIndex;
      const headerRow = sheet.getRow(headerRowIndex);
      columns.forEach((col, index) => {
        const cell = headerRow.getCell(index + 1);
        cell.value = col.head;
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF101557' } };
        cell.alignment = { ...align, horizontal: 'center' };
        cell.border = cellBorder;
      });
      headerRow.height = 24;

      rows.forEach((row, rowOffset) => {
        const excelRow = sheet.getRow(headerRowIndex + 1 + rowOffset);
        columns.forEach((col, index) => {
          const cell = excelRow.getCell(index + 1);
          const raw = col.excel ? col.excel(row) : col.value(row);
          cell.value = raw === '' || raw == null ? null : raw;
          if (raw instanceof Date) cell.numFmt = 'yyyy-mm-dd hh:mm';
          cell.alignment = col.center ? { ...align, horizontal: 'center' } : align;
          cell.border = cellBorder;
          if (rowOffset % 2 === 1) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } };
        });
      });

      sheet.autoFilter = {
        from: { row: headerRowIndex, column: 1 },
        to: { row: headerRowIndex + Math.max(rows.length, 1), column: lastCol },
      };
      sheet.views = [{ rightToLeft: isAr, state: 'frozen', ySplit: headerRowIndex }];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const periodTag = period.period === 'custom' ? `${period.from || 'start'}_${period.to || 'now'}` : period.period;
      const datePart = new Date().toISOString().slice(0, 10);
      downloadBlob(blob, isAr ? `تقرير-التذاكر-${periodTag}-${datePart}.xlsx` : `tickets-report-${periodTag}-${datePart}.xlsx`);
    } catch (err) {
      console.error('Excel export failed:', err);
      window.alert(isAr ? 'تعذر إنشاء ملف Excel، حاول مرة أخرى' : 'Failed to create the Excel file, please try again');
    } finally {
      setExporting(false);
      setExportProgress(null);
    }
  };

  // --- الاستيراد: بيانات المطابقة (أسماء الحالات/الأقسام/العملاء/الموظفين → ids) ---
  const [importOpen, setImportOpen] = useState(false);
  const importLookups = useMemo(() => ({
    statuses: supportSettings?.statuses || [],
    sections: supportSettings?.sections || [],
    products: supportSettings?.products || [],
    tenants: scopedTenants,
    admins: Array.isArray(adminsResult?.data) ? adminsResult.data : [],
    canAssign: canFilterByAssignee,
    userId: user?._id || null,
    lang: currentLangId,
  }), [adminsResult?.data, canFilterByAssignee, currentLangId, scopedTenants, supportSettings, user?._id]);

  const [printing, setPrinting] = useState(false);
  const printReport = async () => {
    if (printing) return;
    const title = isAr ? 'تقرير التذاكر' : 'Tickets Report';
    // بنفتح النافذة فوراً مع الضغطة (عشان مانع النوافذ المنبثقة) وبعدين نملاها بعد ما البيانات تتحمّل.
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<p style="font-family: Arial, sans-serif; padding: 20px;">${isAr ? 'جاري تجهيز التقرير...' : 'Preparing report...'}</p>`);
    setPrinting(true);
    let tickets;
    try {
      tickets = await loadRowsForExport();
    } catch (err) {
      console.error('Print load failed:', err);
      win.close();
      window.alert(isAr ? 'تعذر تحميل بيانات التقرير، حاول مرة أخرى' : 'Failed to load report data, please try again');
      return;
    } finally {
      setPrinting(false);
      setExportProgress(null);
    }
    const { head, body, summary } = buildReportTable(tickets);
    const html = `<html dir="${isAr ? 'rtl' : 'ltr'}"><head><meta charset="utf-8"/><title>${title}</title><style>body { font-family: Arial, sans-serif; padding: 20px; } table { width: 100%; border-collapse: collapse; } th, td { border: 1px solid #e2e8f0; padding: 8px; font-size: 12px; } th { background: #f8fafc; text-align: ${isAr ? 'right' : 'left'}; } .summary { width: auto; margin-bottom: 16px; } .summary td { padding: 4px 10px; } .lbl { font-weight: bold; background: #f8fafc; }</style></head><body><h2>${title}</h2><table class="summary">${summary}</table><table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></body></html>`;
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.onload = () => { win.focus(); win.print(); };
  };

  return (
    <div className="admin-page">
      {/* --- رأس الصفحة --- */}
      <div className="admin-panel flex flex-col md:flex-row justify-between items-start md:items-center p-5 md:p-6">
        <div>
          <h1 className="text-2xl font-bold text-[#08203f] flex items-center gap-3">
            <span className="bg-[#fff5d6] text-[#08203f] p-2 rounded-lg border border-[#f5b800]/25">📊</span>
            {isAr ? 'تقارير التذاكر' : 'Tickets Reports'}
          </h1>
          <p className="text-slate-500 text-sm mt-1">{isAr ? 'تحليل ومتابعة أداء التذاكر' : 'Analyze and track ticket performance'}</p>
        </div>
      </div>

      <div className="admin-panel p-3 md:p-4 flex flex-col gap-4 min-w-0">
        {/* --- نفس شريط فلاتر /tickets --- */}
        <TicketFiltersBar
          lang={currentLangId}
          filters={filters}
          options={filterOptions}
          showAssignee={canFilterByAssignee}
          onFilterChange={(key, value) => updateParams((next) => {
            if (!value || value === 'all') next.delete(key);
            else next.set(key, String(value));
          })}
          period={period.period}
          from={period.from}
          to={period.to}
          onPeriodChange={({ period: nextPeriod, from, to }) => updateParams((next) => {
            next.set('period', nextPeriod);
            if (nextPeriod === 'custom' && from) next.set('from', from);
            else next.delete('from');
            if (nextPeriod === 'custom' && to) next.set('to', to);
            else next.delete('to');
          })}
          statuses={supportSettings?.statuses}
          activeStatus={reportStatus}
          getStatusCount={getStatusCount}
          onStatusChange={(value) => updateParams((next) => {
            if (!value || value === 'all') next.delete('status');
            else next.set('status', String(value));
          })}
          searchValue={reportSearch}
          onSearchChange={setReportSearch}
        />

        {/* --- أزرار الإجراءات (Excel و طباعة واستيراد) + الترقيم --- */}
        <div className="flex justify-between items-center gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-bold text-slate-700">
              {isAr ? `نتائج التقرير: ${reportTotal} تذكرة` : `Report Results: ${reportTotal} Tickets`}
            </span>
            {selected.size > 0 && (
              <span className="inline-flex items-center gap-2 text-xs font-semibold text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-3 py-1">
                {isAr ? `${selected.size} محددة` : `${selected.size} selected`}
                <button type="button" onClick={() => setSelected(new Map())} className="text-sky-500 hover:text-sky-800" aria-label={isAr ? 'إلغاء التحديد' : 'Clear selection'}>
                  <MdClose size={14} />
                </button>
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <MwButton type="saveBtn" onClick={exportReportExcel} disabled={exporting || printing || reportLoading || reportTotal === 0} className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white">
              <MdFileDownload size={18} />
              {exporting
                ? `${isAr ? 'جاري التصدير...' : 'Exporting...'}${exportProgress ? ` ${exportProgress.done}/${exportProgress.total}` : ''}`
                : selected.size
                  ? (isAr ? `تحميل Excel (${selected.size} محددة)` : `Download Excel (${selected.size} selected)`)
                  : (isAr ? 'تحميل Excel' : 'Download Excel')}
            </MwButton>
            <MwButton type="secondary" onClick={() => setImportOpen(true)}>
              <MdUploadFile size={18} /> {isAr ? 'استيراد Excel' : 'Import Excel'}
            </MwButton>
            <MwButton type="saveBtn" onClick={printReport} disabled={printing || exporting || reportLoading || reportTotal === 0} className="flex items-center gap-2 bg-slate-700 hover:bg-slate-800 text-white">
              <MdPrint size={18} /> {printing ? (isAr ? 'جاري التجهيز...' : 'Preparing...') : (isAr ? 'طباعة' : 'Print')}
            </MwButton>
            <ReportsPager page={page} pageSize={TABLE_PAGE_SIZE} total={reportTotal} onPageChange={setPage} isAr={isAr} disabled={reportLoading} />
          </div>
        </div>

        {/* --- جدول البيانات --- */}
        <div className="table-scroll max-w-full max-h-[70vh] overflow-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
          {reportLoading ? (
            <div className="flex items-center justify-center p-16">
              <MwSpinnerButton withLabel={true} isFullCenter />
            </div>
          ) : reportError ? (
            <div className="text-center py-16 text-red-500 bg-white text-sm font-medium">
              {isAr ? 'تعذر تحميل بيانات التقرير، حاول مرة أخرى' : 'Failed to load report data, please try again'}
            </div>
          ) : reportRows.length === 0 ? (
            <div className="text-center py-16 text-slate-500 bg-white">
              <div className="text-4xl mb-3">📭</div>
              <p className="font-medium">{isAr ? 'لا توجد بيانات مطابقة للفلاتر المحددة' : 'No data matches the selected filters'}</p>
            </div>
          ) : (
            <table className="w-full min-w-max text-sm text-left rtl:text-right text-slate-600">
              <thead className="text-xs text-slate-700 uppercase bg-slate-100 border-b border-slate-200 sticky top-0 z-10">
                <tr>
                  <th className="ps-4 pe-1 py-3 w-8">
                    <input ref={selectAllPageRef} type="checkbox" className="w-4 h-4 cursor-pointer accent-emerald-600 align-middle" checked={allPageSelected} onChange={togglePage} aria-label={isAr ? 'تحديد الصفحة' : 'Select page'} />
                  </th>
                  <th className="px-4 py-3 font-bold whitespace-nowrap">{isAr ? 'رقم التذكرة' : 'Ticket #'}</th>
                  <th className="px-4 py-3 font-bold">{isAr ? 'العنوان' : 'Subject'}</th>
                  <th className="px-4 py-3 font-bold">{isAr ? 'العميل' : 'Customer'}</th>
                  <th className="px-4 py-3 font-bold">{isAr ? 'القسم' : 'Section'}</th>
                  <th className="px-4 py-3 font-bold">{isAr ? 'المنتج' : 'Product'}</th>
                  {canFilterByAssignee && (
                    <th className="px-4 py-3 font-bold">{isAr ? 'معينة لـ' : 'Assigned To'}</th>
                  )}
                  <th className="px-4 py-3 font-bold">{isAr ? 'تاريخ الإنشاء' : 'Created At'}</th>
                  <th className="px-4 py-3 font-bold">{isAr ? 'الحالة' : 'Status'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {reportRows.map((ticket) => {
                  const row = getReportTicketRow(ticket);
                  const statusStyle = getStatusAppearance(supportSettings?.statuses, ticket?.statusId || ticket?.status);
                  const isSelected = selected.has(String(ticket?._id));
                  return (
                    <tr key={ticket?._id || row.ticketNumber} className={`transition-colors ${isSelected ? 'bg-sky-50/70' : 'hover:bg-slate-50'}`}>
                      <td className="ps-4 pe-1 py-3">
                        <input type="checkbox" className="w-4 h-4 cursor-pointer accent-emerald-600 align-middle" checked={isSelected} onChange={() => toggleTicket(ticket)} aria-label={`${isAr ? 'تحديد التذكرة' : 'Select ticket'} ${row.ticketNumber}`} />
                      </td>
                      <td className="px-4 py-3 font-mono font-medium text-slate-900 whitespace-nowrap">{row.ticketNumber}</td>
                      <td className="px-4 py-3 font-medium text-slate-800"><TruncatedText text={row.subject} style={{ maxWidth: 340 }} /></td>
                      <td className="px-4 py-3"><TruncatedText text={row.tenant} style={{ maxWidth: 200 }} /></td>
                      <td className="px-4 py-3"><TruncatedText text={row.section} style={{ maxWidth: 160 }} /></td>
                      <td className="px-4 py-3"><TruncatedText text={row.product} style={{ maxWidth: 180 }} /></td>
                      {canFilterByAssignee && (
                        <td className="px-4 py-3"><TruncatedText text={row.assignee} style={{ maxWidth: 200 }} /></td>
                      )}
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{row.createdAt}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap" style={{ backgroundColor: statusStyle?.bg, color: statusStyle?.color }}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusStyle?.dot || statusStyle?.color }} />
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {!reportLoading && !reportError && reportTotal > TABLE_PAGE_SIZE && (
          <div className="flex justify-end">
            <ReportsPager page={page} pageSize={TABLE_PAGE_SIZE} total={reportTotal} onPageChange={setPage} isAr={isAr} />
          </div>
        )}
      </div>

      <TicketImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => { setSelected(new Map()); setReloadKey((key) => key + 1); }}
        lookups={importLookups}
        isAr={isAr}
      />
    </div>
  );
};

export default Reports;
